import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { customerApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Logo from "@/components/layout/Logo";
import { PORTAL_TABS as ENGINE_TABS, isPortalTabActive } from "@/lib/ClientPortalEngine";
import { Sparkles, Pencil } from "lucide-react";
import LoyaltyHistoryDialog from "@/components/portal/LoyaltyHistoryDialog";
import EditMyInfoDialog from "@/components/portal/EditMyInfoDialog";

/**
 * Client-portal navigation.
 *
 * ClientPortalEngine.jsx is IMMUTABLE per SYSTEM_RULES.md. The engine
 * defines the 4 tabs and their labels. To rename "Profile" → "My Journey"
 * without touching the engine we apply a consumer-layer LABEL_OVERRIDES
 * map here — the engine stays sealed, only the visible label flips.
 *
 * Also owns:
 *   - the persistent Loyalty Balance chip in the top-right (opens the
 *     Loyalty History dialog on click);
 *   - the "Hi, {name}" greeting chip (opens the Edit My Info dialog).
 * Both live at the navigation layer so they render on every portal
 * page (Book, My Visit, Concierge, My Journey).
 */

// Consumer-layer label overrides. Engine remains sealed.
const LABEL_OVERRIDES = Object.freeze({
  "nav-profile": "My Journey",
});

const displayLabel = (t) => LABEL_OVERRIDES[t.testid] || t.label;

const nfmt = (n) => Number(n || 0).toLocaleString("en-GB");

export default function CustomerNav() {
  const { customer, logoutCustomer, setCustomerFromToken } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();

  const [balance, setBalance] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const isActive = (to) => isPortalTabActive(pathname, to);

  // Loyalty balance sync — mirrors CustomerProfile's polling contract
  // (SYSTEM_RULES · Loyalty Balance Sync). Fetches on mount, on
  // visibilitychange -> visible, on window.focus, on the
  // `cocoon:loyalty-updated` broadcast (staff redeem/adjust), and
  // every 30s while the tab is visible.
  const fetchBalance = useCallback(() => {
    if (!customer) return;
    customerApi
      .get("/customers/me/loyalty/expiring")
      .then((r) => setBalance(Number(r.data?.balance || 0)))
      .catch(() => { /* keep prior value on transient error */ });
  }, [customer]);

  useEffect(() => {
    if (!customer) { setBalance(null); return; }
    fetchBalance();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchBalance();
    }, 30_000);
    const onVis = () => { if (document.visibilityState === "visible") fetchBalance(); };
    const onLoyalty = () => fetchBalance();
    const onCust = () => fetchBalance();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    window.addEventListener("cocoon:loyalty-updated", onLoyalty);
    window.addEventListener("cocoon:customer-updated", onCust);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      window.removeEventListener("cocoon:loyalty-updated", onLoyalty);
      window.removeEventListener("cocoon:customer-updated", onCust);
    };
  }, [customer, fetchBalance]);

  const firstName = (customer?.first_name || customer?.full_name || "").split(" ")[0];

  const onSavedInfo = (fresh) => {
    if (fresh && typeof setCustomerFromToken === "function") {
      setCustomerFromToken(fresh);
    }
  };

  const greetingChip = customer && (
    <button
      type="button"
      onClick={() => setEditOpen(true)}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
      data-testid="header-edit-info-btn"
    >
      <span>Hi, <span className="text-foreground font-medium">{firstName}</span></span>
      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );

  const pointsChip = customer && balance !== null && (
    <button
      type="button"
      onClick={() => setHistoryOpen(true)}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100 transition-colors text-xs sm:text-sm font-medium"
      data-testid="header-loyalty-balance"
    >
      <Sparkles className="w-3.5 h-3.5" />
      <span data-testid="header-loyalty-balance-value">{nfmt(balance)}</span>
      <span className="text-[10px] sm:text-xs opacity-80">pts</span>
    </button>
  );

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-background/95 border-b border-border shadow-sm"
        data-testid="customer-nav-header"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between gap-3">
          <Logo variant="mark" to="/" size={36} testid="brand-logo-link" />

          {customer && (
            <nav
              className="hidden md:flex items-center gap-8"
              data-testid="customer-nav-desktop"
            >
              {ENGINE_TABS.map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={t.testid}
                  className={
                    "text-sm tracking-overline transition-colors duration-200 " +
                    (isActive(t.to)
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground")
                  }
                >
                  {displayLabel(t)}
                </Link>
              ))}
            </nav>
          )}

          <div className="flex items-center gap-2 sm:gap-3">
            {customer ? (
              <>
                {/* Desktop: greeting + points chip side by side. */}
                <div className="hidden md:flex items-center gap-3">
                  {greetingChip}
                  {balance !== null && <span className="text-muted-foreground/50">|</span>}
                  {pointsChip}
                </div>
                {/* Mobile: points chip only (greeting is tap target in the nav row below). */}
                <div className="md:hidden">
                  {pointsChip}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full hidden md:inline-flex"
                  data-testid="customer-logout-btn"
                  onClick={() => { logoutCustomer(); nav("/"); }}
                >
                  Sign out
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="md:hidden rounded-full text-[11px] text-muted-foreground hover:text-foreground"
                  data-testid="mobile-logout-btn"
                  onClick={() => { logoutCustomer(); nav("/"); }}
                >
                  Sign out
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                className="rounded-full"
                onClick={() => nav("/portal/login")}
                data-testid="customer-signin-btn"
              >
                Sign in
              </Button>
            )}
          </div>
        </div>

        {/* Mobile greeting strip — separate row above the tab bar so the guest
            still has a tap-target to open Edit My Info on small screens. */}
        {customer && (
          <div
            className="md:hidden px-4 py-1.5 border-t border-border/60 bg-background/95 backdrop-blur flex items-center justify-between"
            data-testid="customer-nav-mobile-greeting-row"
          >
            {greetingChip}
          </div>
        )}

        {/* Mobile tab row — always visible when signed in, replaces sandwich menu. */}
        {customer && (
          <nav
            className="md:hidden border-t border-border/70 bg-background/95 backdrop-blur"
            data-testid="customer-nav-mobile"
          >
            <div className="max-w-7xl mx-auto grid grid-cols-4">
              {ENGINE_TABS.map((t) => {
                const Icon = t.icon;
                const active = isActive(t.to);
                return (
                  <Link
                    key={t.to}
                    to={t.to}
                    data-testid={`${t.testid}-mobile`}
                    className={
                      "flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] font-medium " +
                      "transition-colors duration-150 " +
                      (active
                        ? "text-foreground border-b-2 border-primary"
                        : "text-muted-foreground border-b-2 border-transparent hover:text-foreground")
                    }
                  >
                    <Icon className={"w-4 h-4 " + (active ? "text-primary" : "")} />
                    <span className="leading-none">{displayLabel(t)}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </header>

      {/*
        Layout spacer that matches the fixed header's height so the page
        content never scrolls under the sticky tabs.

          • Signed-in mobile: 56px header + 30px greeting row + 52px tab row = 138px
          • Signed-in desktop (md+): single 64px header only
          • Signed-out: 56px header (mobile) / 64px (desktop)
      */}
      <div
        aria-hidden
        className={
          customer
            ? "h-[138px] md:h-16"
            : "h-14 md:h-16"
        }
        data-testid="customer-nav-spacer"
      />

      {customer && (
        <>
          <LoyaltyHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} />
          <EditMyInfoDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            customer={customer}
            onSaved={onSavedInfo}
          />
        </>
      )}
    </>
  );
}
